// User interfaces matching the backend API models
import { PagedResult } from './common.models';
import type { EmployeePracticeAreaDto, ResourceDto } from './resource.models';
import { EmployeePracticeAreaSource } from './resource.models';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  employeeId?: string | null;
  azureAdObjectId: string | null;
  createdAt: string; // ISO date string from API
  updatedAt: string | null; // ISO date string from API
  lastLogin: string | null; // ISO date string from API
  isActive: boolean;
  emailNotificationsEnabled: boolean;
  preferredEmail?: string | null;
  userRoles?: UserRole[];
}

/**
 * The current user's "resource capacity reminder" email preference plus the office-derived context the
 * profile toggle needs. `visible` = the primary office has the notification enabled (else hide the
 * toggle); `locked` = the office set RequireEmailForAllResources (show on + read-only).
 */
export interface CapacityReminderEmailPreference {
  visible: boolean;
  locked: boolean;
  emailsEnabled: boolean;
}

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  azureAdObjectId?: string | null;
}

export interface UpdateUserRequest {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  azureAdObjectId?: string | null;
  lastLogin?: string | null;
  isActive?: boolean | null;
  emailNotificationsEnabled?: boolean | null;
}

export interface Permission {
  id: number;
  name: string;
  description: string;
  category?: string | null;
  isActive: boolean;
  sortOrder: number;
}

// New entity interfaces for relational data
export interface Role {
  id: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string | null;
  isActive: boolean;
  isDefaultUser?: boolean;
  isProtectedAdmin?: boolean;
  permissions?: Permission[];
}

export interface PracticeArea {
  practiceCode: string;
  description: string;
  originalDescription?: string;
  isActive: boolean;
  /**
   * True when the practice area was created in this app rather than synced from the firm
   * directory. The API sets it from the stored record, never from the code's shape.
   */
  isCustom?: boolean;
  assignedUsers?: User[];
  assignedResources?: ResourceDto[];
}

export interface UserRole {
  userId: number;
  roleId: number;
  assignedAt: string;
  isActive: boolean;
  role?: Role;
}

// Extended user interface with relational data
export interface UserWithRelations extends User {
  userRoles?: UserRole[];
}

// Frontend-specific interfaces for UI state management
export interface UserListState {
  users: User[];
  filteredUsers: User[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

// Login-specific interfaces
export interface LoginRequest {
  // Empty for now - user info comes from JWT claims
}

export interface LoginResponse {
  user: User;
  roles: Role[];
  practiceAreas: PracticeArea[];
  department?: string;
  isNewUser: boolean;
  message: string;
}

// User search interfaces
export interface UserSearchRequest {
  query?: string;
  excludePracticeAreaCode?: string;
  pageIndex?: number;
  pageSize?: number;
  activeOnly?: boolean;
}

export interface UserSearchResult {
  users: User[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Paginated user interfaces (following the same pattern as resources)
export interface UserDto {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  employeeId?: string | null;
  azureAdObjectId: string | null;
  lastLogin: string | null; // ISO date string from API
  createdAt: string; // ISO date string from API
  updatedAt: string | null; // ISO date string from API
  isActive: boolean;
  roles: UserRoleDto[];
  practiceAreas: EmployeePracticeAreaDto[];
  /** Offices the user's resource belongs to (primary + secondary). */
  offices?: UserOfficeDto[];
  /**
   * True when this row represents an employee/resource without an application user account
   * (surfaced in the directory so managers can assign offices/practice areas before first login).
   * Resource-only rows always render as Basic + Inactive and cannot have their role changed.
   */
  isResourceOnly?: boolean;
  /**
   * The resource's explicit resource-visibility override, as a RESOURCE_VISIBILITY_MODE
   * lookup code. Null/absent when the resource uses the system default.
   */
  visibilityModeCode?: string | null;
  /** Display name of visibilityModeCode from the backend lookup, or null when using the default. */
  visibilityModeName?: string | null;
}

/**
 * Read-only directory projection of an office a user belongs to. Intentionally distinct from
 * `OfficeMembershipDto` (resource.models.ts): that one also carries `secondaryAssociationId` for
 * office administration. The directory only displays offices and gets removal ids from a separate
 * call, so it deliberately omits that field. Do not merge — separate read models, different jobs.
 */
export interface UserOfficeDto {
  officeCode: string;
  officeName: string;
  isPrimary: boolean;
}

export interface UserRoleDto {
  roleId: number;
  roleName: string;
  isActive: boolean;
  assignedAt: string; // ISO date string from API
}

/**
 * A resource-visibility mode option shown in the Resource Visibility dialog.
 * Mirrors a backend `RESOURCE_VISIBILITY_MODE` lookup value (codes:
 * PRACTICE_AREA, OFFICE_ONLY, OFFICE_AND_PRACTICE_AREA); the dialog builds these
 * from the lookup (label = value name, description = value description) so the
 * copy stays in sync with the seeded lookup.
 *
 * These are the explicit, per-resource overrides an admin can pick (persisted
 * via PUT/DELETE api/resources/visibility/{employeeId}). A resource can instead
 * use the system default policy, represented by SYSTEM_DEFAULT_VISIBILITY_CODE
 * (persisted by clearing the override).
 */
export interface VisibilityModeOption {
  /** Lookup code shared with the backend RESOURCE_VISIBILITY_MODE values. */
  code: string;
  /** Display label shown in the dialog (the lookup value's name). */
  label: string;
  /** One-line explanation shown beneath the option label (the lookup value's description). */
  description: string;
  /** Whether this is the system-default mode (the lookup value flagged `isDefault`). */
  isDefault: boolean;
}

/**
 * Synthetic option used in the Resource Visibility dialog: use the system
 * default rather than setting an explicit per-resource override. Not a backend
 * lookup code — selecting it clears any override (DELETE) so the backend
 * cascade falls back to the system default.
 */
export const SYSTEM_DEFAULT_VISIBILITY_CODE = 'SYSTEM_DEFAULT';

/**
 * A per-user resource-visibility override as returned by the admin CRUD endpoints
 * (GET/PUT api/resources/visibility/{employeeId}). The mode is identified by its
 * RESOURCE_VISIBILITY_MODE lookup code.
 */
export interface VisibilityResourceOverrideDto {
  employeeId: string;
  modeCode: string;
  modeName: string;
}

export { EmployeePracticeAreaSource };
export type { EmployeePracticeAreaDto };

export interface UserListRequest {
  page: number;
  pageSize: number;
  search?: string;
  practiceAreaCode?: string;
  isActive?: boolean;
  roleId?: number;
  sortBy?: string;
  sortDirection?: string;
}

export type { PagedResult };

